import { useQuery, UseQueryResult } from "@tanstack/react-query";
import saveAs from "file-saver";
import config from "../config.json";
import { IPageable, Option, Params } from "../types/table/interfaces.type";
import { createAxiosInstance } from "./ServicesHelpers";

const axiosInstance = createAxiosInstance(config.ADMIN_API_BASE_URL);

export interface RoleType {
  id: string;
  name: string;
  role: string;
  isDeleted: boolean;
  createdDate: string;
  createdBy: string;
  updatedBy?: string;
}

export const exportRole = async (
  params: Params<RoleType & { fromDate?: string; toDate?: string }>,
): Promise<void> => {
  try {
    const response = await axiosInstance.get("/roles/csv", {
      params,
      responseType: "blob",
    });

    const disposition = response.headers["content-disposition"];
    let filename = "Role.csv";
    if (disposition) {
      const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (match && match[1]) {
        filename = match[1].replace(/['"]/g, "").trim();
      }
    }

    const blob = new Blob([response.data], { type: "text/csv" });
    saveAs(blob, filename);

    console.log("Export successful and file downloaded.");
  } catch (error: any) {
    console.error(
      "Error exporting Role:",
      error.response?.data?.message || error.message || error,
    );
  }
};

export async function fetchRoles(
  params: Params<RoleType>,
): Promise<IPageable<RoleType>> {
  try {
    const response = await axiosInstance.get("/roles", { params });
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while getting the users.",
    );
  }
}

export async function fetchRole(id: RoleType["id"]): Promise<RoleType> {
  try {
    const response = await axiosInstance.get(`/roles/${id}`);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        `An error occurred while getting routing-plan/${id}.`,
    );
  }
}

export const addRoleApi = async (data: Partial<RoleType>) => {
  try {
    const { role, ...body } = data;
    const response = await axiosInstance.post("/roles", body, {
      params: { role },
    });
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while adding the Role.",
    );
  }
};

export const editRoleApi = async (id: string, data: Partial<RoleType>) => {
  try {
    const { role, ...body } = data;

    const response = await axiosInstance.put(`/roles/${id}`, body, {
      params: { role },
    });
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while updating the Role.",
    );
  }
};

export const getRoleById = async (id: string) => {
  try {
    const response = await axiosInstance.get(`/roles/${id}`);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while fetching the Role.",
    );
  }
};

export const deleteRolesApi = async (ids: string[]): Promise<void> => {
  try {
    await axiosInstance.delete("/roles", { data: ids });
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while deleting multiple Roles.",
    );
  }
};

export const useRoleQuery = (
  params: Params<RoleType>,
): UseQueryResult<IPageable<RoleType>, Error> => {
  const effectiveParams = {
    ...params,
    sort: params.sort || "createdDate,desc",
  };
  const queryKey = ["Role", JSON.stringify(effectiveParams)];
  return useQuery({
    queryKey,
    queryFn: () => fetchRoles(effectiveParams),
  });
};

export const useRolesQuery = (): UseQueryResult<Option[], Error> => {
  const queryKey = ["Roles", JSON.stringify({ sort: "name", size: 10000 })];
  return useQuery({
    queryKey,
    queryFn: async () => {
      const Roles = await fetchRoles({ sort: "name", size: 10000 });
      const RoleOptions: Option[] = Roles.content.map((plan: any) => ({
        label: plan.name,
        value: plan.id,
      }));
      return RoleOptions;
    },
  });
};

export const getRoleName = (RoleId: string) => ({
  queryKey: ["Role", RoleId],
  queryFn: async (): Promise<string> => {
    const Role = await fetchRole(RoleId);
    return Role?.name;
  },
  select: (name: string) => name,
});
